'use client'

import { useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import { useCallback } from 'react'

import { resetGeneralSettingsSyncState } from '@/features/dashboard/hooks/use-general-settings-sync'
import { resetPageTabsSyncState } from '@/features/dashboard/hooks/use-page-tabs-sync'
import { resetGuidesSyncState } from '@/features/onboarding/hooks/use-guides-sync'
import { queryClient } from '@/provider/query-provider'
import { clearChatThreadsAtom } from '@/state/chat'
import {
  activePageIdAtom,
  dashboardLayoutsAtom,
  pendingDashboardPageNavigationAtom,
} from '@/state/dashboard-layouts'
import { notificationHistoryRawAtom, notificationLastSeenAtAtom } from '@/state/notifications'
import {
  guidePreferencesReadyAtom,
  welcomeGuideAtom,
  welcomeTourSessionAtom,
} from '@/state/onboarding'
import { forgetOptionsMmAtoms } from '@/state/options-mm'
import {
  lastOrderAccountByTradeVenueAtom,
  orderFormPopupCardIdAtom,
  orderFormPopupConfigAtom,
} from '@/state/order'
import { positionGroupStateAtom } from '@/state/position-group'
import { presetTourDoneAtom } from '@/state/preset-tour'
import { clearResearchRuntimeAtom } from '@/state/research'
import { clearRfqMakerRuntimeAtom } from '@/state/rfq'
import {
  dashboardConfigAtom,
  dashboardConfigAtomFamily,
  exchangeAtom,
  hiddenPageIdsAtom,
  pageAutoHideDaysAtom,
  pageLastUsedAtAtom,
  pageTabsOrderAtom,
  pinnedPageIdsAtom,
  recentOptionTickersAtom,
  recentWatchTickersAtom,
  resetAllStorage,
  watchListAtom,
} from '@/state/storage'
import { tutorialProgressAtom } from '@/state/tutorial'
import { venueTelemetryAtom } from '@/state/venue-telemetry'
import { mobileCollapsedAtom } from '@/state/widget-actions'

export function useResetClientStorage() {
  // useResetAtom (not useSetAtom + a hand-copied default) for every
  // atomWithStorage atom below — it resets the in-memory value AND removes
  // the underlying (namespaced) storage key in one call, straight from each
  // atom's own initial value, so a default declared in its own state file
  // never has to be duplicated here.
  const resetDashboardLayouts = useResetAtom(dashboardLayoutsAtom)
  const resetActivePageId = useResetAtom(activePageIdAtom)
  const setPendingDashboardPageNavigation = useSetAtom(pendingDashboardPageNavigationAtom)
  const resetPageTabsOrder = useResetAtom(pageTabsOrderAtom)
  const resetPageLastUsedAt = useResetAtom(pageLastUsedAtAtom)
  const resetPinnedPageIds = useResetAtom(pinnedPageIdsAtom)
  const resetHiddenPageIds = useResetAtom(hiddenPageIdsAtom)
  const resetPageAutoHideDays = useResetAtom(pageAutoHideDaysAtom)
  const resetExchange = useResetAtom(exchangeAtom)
  const resetWatchList = useResetAtom(watchListAtom)
  const resetDashboardConfig = useResetAtom(dashboardConfigAtom)
  // Risk Slides / Delta Hedge's own standalone Positions-table column config (see
  // dashboard-config-scope-context.ts) — resetAllStorage's wildcard scan below also
  // clears their localStorage keys, but that alone leaves an already-mounted atom's
  // in-memory value (and thus what's rendered) stale for this store's lifetime.
  const resetRiskSlidesDashboardConfig = useResetAtom(dashboardConfigAtomFamily('risk-slides'))
  const resetHedgeDashboardConfig = useResetAtom(dashboardConfigAtomFamily('hedge'))
  const resetRecentWatchTickers = useResetAtom(recentWatchTickersAtom)
  const resetRecentOptionTickers = useResetAtom(recentOptionTickersAtom)
  const resetPositionGroupState = useResetAtom(positionGroupStateAtom)
  const resetMobileCollapsed = useResetAtom(mobileCollapsedAtom)
  const resetOrderFormPopupConfig = useResetAtom(orderFormPopupConfigAtom)
  const resetLastOrderAccountByTradeVenue = useResetAtom(lastOrderAccountByTradeVenueAtom)
  // orderFormPopupCardIdAtom is a derived atom wrapping a private atomWithStorage
  // (state/order.ts) with a custom write function, so useResetAtom's RESET signal
  // isn't guaranteed to reach the underlying storage the way it does for a plain
  // atomWithStorage — set it to its own default (null) explicitly instead.
  const setOrderFormPopupCardId = useSetAtom(orderFormPopupCardIdAtom)
  const clearChatThreads = useSetAtom(clearChatThreadsAtom)
  const clearResearchRuntime = useSetAtom(clearResearchRuntimeAtom)
  const clearRfqMakerRuntime = useSetAtom(clearRfqMakerRuntimeAtom)
  const clearNotificationHistory = useSetAtom(notificationHistoryRawAtom)
  const clearVenueTelemetry = useSetAtom(venueTelemetryAtom)
  const setNotificationLastSeenAt = useSetAtom(notificationLastSeenAtAtom)
  // Guide state (welcome tour, tutorial progress) is per user and re-loaded from the
  // server on the next login (useGuidesSync); the in-memory copies must not carry over.
  const resetWelcomeGuide = useResetAtom(welcomeGuideAtom)
  const resetWelcomeTourSession = useResetAtom(welcomeTourSessionAtom)
  const resetTutorialProgress = useResetAtom(tutorialProgressAtom)
  const setGuidePreferencesReady = useSetAtom(guidePreferencesReadyAtom)
  const setPresetTourDone = useSetAtom(presetTourDoneAtom)

  return useCallback(
    (userId?: string) => {
      resetAllStorage(userId)
      resetDashboardLayouts()
      resetActivePageId()
      setPendingDashboardPageNavigation(null)
      resetPageTabsOrder()
      resetPageLastUsedAt()
      resetPinnedPageIds()
      resetHiddenPageIds()
      resetPageAutoHideDays()
      // The atom reset above wipes the local order; the sync module's once-per-user
      // flags must drop with it, or a same-user re-login in this JS session would
      // skip re-hydrating the server-saved order.
      resetPageTabsSyncState()
      // General settings re-hydrate from the server on the next login (useGeneralSettingsSync).
      resetGeneralSettingsSyncState()
      resetWelcomeGuide()
      resetWelcomeTourSession()
      resetTutorialProgress()
      setGuidePreferencesReady(false)
      setPresetTourDone(false)
      resetGuidesSyncState()
      // Every atomWithStorage atom read directly by dashboard widgets (selected
      // account, watchlist, column config, recent-ticker pickers) needs its own
      // reset here — atomWithStorage only re-reads localStorage once per store
      // lifetime, so on a shared browser its in-memory value survives a
      // soft-navigated logout/login handoff (no full reload happens here) and
      // would otherwise keep showing the outgoing user's state to whoever logs
      // in next. `settings-sound` is deliberately excluded: it isn't namespaced
      // per user (see safe-storage.ts), so it's a device-level preference, not
      // per-user state.
      resetExchange()
      resetWatchList()
      resetDashboardConfig()
      resetRiskSlidesDashboardConfig()
      resetHedgeDashboardConfig()
      resetRecentWatchTickers()
      resetRecentOptionTickers()
      resetPositionGroupState()
      resetMobileCollapsed()
      resetOrderFormPopupConfig()
      resetLastOrderAccountByTradeVenue()
      setOrderFormPopupCardId(null)
      // All live atoms are cleared so a soft login as another account cannot render or resume
      // the previous account's jobs; its history stays on the server, scoped to that user.
      clearChatThreads()
      clearResearchRuntime()
      clearRfqMakerRuntime()
      clearNotificationHistory([])
      clearVenueTelemetry({})
      forgetOptionsMmAtoms()
      setNotificationLastSeenAt(0)
      // Wipe all React Query cache — without this, queries keyed by the previous
      // session (e.g. accounts) keep serving stale data to whoever logs in next,
      // since nothing else here touches the query cache at all.
      queryClient.clear()
    },
    [
      clearChatThreads,
      clearResearchRuntime,
      clearRfqMakerRuntime,
      clearNotificationHistory,
      clearVenueTelemetry,
      resetActivePageId,
      resetDashboardConfig,
      resetRiskSlidesDashboardConfig,
      resetHedgeDashboardConfig,
      resetDashboardLayouts,
      resetExchange,
      resetHiddenPageIds,
      resetLastOrderAccountByTradeVenue,
      resetMobileCollapsed,
      resetOrderFormPopupConfig,
      resetPageAutoHideDays,
      resetPageLastUsedAt,
      resetWelcomeGuide,
      resetWelcomeTourSession,
      resetTutorialProgress,
      setGuidePreferencesReady,
      setPresetTourDone,
      resetPageTabsOrder,
      resetPinnedPageIds,
      resetPositionGroupState,
      resetRecentOptionTickers,
      resetRecentWatchTickers,
      resetWatchList,
      setOrderFormPopupCardId,
      setNotificationLastSeenAt,
      setPendingDashboardPageNavigation,
    ]
  )
}
